import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { LanguageCode } from '@elfsight/configurator-contracts';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import useMergedRef from '@react-hook/merged-ref';
import { type CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { useRect } from 'react-use-rect';
import { commonFieldsSettings } from '../commonFieldSettings';
import type { ShortTextProps } from '../short-text';
import { CountryCodeDropdown, PhoneInput } from './components';
import { COUNTRIES } from './countries';
import phoneNumberFlagUrl from './icons/phone-number-flag.svg';
import { type Country } from './types';
import { getDefaultCountry, PhoneCountryDetector } from './utility';

export type PhoneProps = ShortTextProps & {
  countries: Country[];
  showPlaceholder: boolean;
  fieldActiveColor?: string;
  widgetLanguage?: LanguageCode;
  searchPlaceholderText?: string;
  emptyCountryListMessage?: string;
  ariaLabelCountryDropdown?: string;
  ariaLabelCountryDropdownTrigger?: string;
  portalContainerElement?: Element;
  defaultCountry?: {
    mode: 'auto' | 'manual';
    countryCode?: CountryCode;
  };
};

export interface CountryChangeMetaRef {
  changedByCountryList: boolean;
  changedByConfigurator: boolean;
}

export const Phone = forwardRef<TextControlBaseInstance, PhoneProps>(
  (
    {
      fieldColor = commonFieldsSettings.fieldColor,
      fieldFillColor = commonFieldsSettings.fieldFillColor,
      fieldBorderType = commonFieldsSettings.fieldBorderType,
      fieldActiveColor = commonFieldsSettings.fieldActiveColor,
      fieldBorderRadius = commonFieldsSettings.fieldBorderRadius,
      showPlaceholder = true,
      searchPlaceholderText: derivedSearchPlaceholderText,
      emptyCountryListMessage: derivedEmptyCountryListMessage,
      ariaLabelCountryDropdown: derivedAriaLabelCountryDropdown,
      ariaLabelCountryDropdownTrigger: derivedAriaLabelCountryDropdownTrigger,
      countries = COUNTRIES,
      widgetLanguage,
      defaultCountry = { mode: 'auto' as const },
      value,
      portalContainerElement,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { t } = useLingui();

    const searchPlaceholderText =
      derivedSearchPlaceholderText ?? t`Search country...`;
    const emptyCountryListMessage =
      derivedEmptyCountryListMessage ?? t`No countries found`;
    const ariaLabelCountryDropdown =
      derivedAriaLabelCountryDropdown ?? t`Country selection list`;
    const ariaLabelCountryDropdownTrigger =
      derivedAriaLabelCountryDropdownTrigger ?? t`Toggle country selection`;
    const [open, setOpen] = useState(false);
    const detectorRef = useRef(new PhoneCountryDetector());

    const getInitialCountry = (): Country => {
      if (defaultCountry.mode === 'manual' && defaultCountry.countryCode) {
        const manualCountry = countries.find(
          (country) => country.countryCode === defaultCountry.countryCode
        );
        if (manualCountry) {
          return manualCountry;
        }
      }

      // Auto mode: use detector first, then navigator.language (user's locale)
      // widgetLanguage is only used as fallback if navigator.language is not available
      const detectedCode = detectorRef.current.getCountryCode();
      if (detectedCode) {
        const detectedCountry = countries.find(
          (country) => country.countryCode === detectedCode
        );
        if (detectedCountry) {
          return detectedCountry;
        }
      }

      const languageToUse = navigator.language || widgetLanguage || 'en-US';
      return getDefaultCountry(languageToUse, countries);
    };

    const [selectedCountry, setSelectedCountry] =
      useState<Country>(getInitialCountry());
    const countryChangeMetaRef = useRef({
      changedByCountryList: false,
      changedByConfigurator: false
    });
    const [anchorElement, setAnchorElement] = useState<Element | null>(null);
    const [width, setWidth] = useState(0);
    const [measureRef] = useRect(({ width }) => setWidth(width), {
      resize: true
    });
    const mergedRef = useMergedRef(setAnchorElement, measureRef);
    const flagContainerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (value) {
        detectorRef.current.input(value);
      }
    }, [value]);

    // Update country when defaultCountry changes
    useEffect(() => {
      if (defaultCountry.mode === 'manual' && defaultCountry.countryCode) {
        const manualCountry = countries.find(
          (c) => c.countryCode === defaultCountry.countryCode
        );
        if (
          manualCountry &&
          manualCountry.countryCode !== selectedCountry.countryCode
        ) {
          countryChangeMetaRef.current.changedByConfigurator = true;
          detectorRef.current.input(manualCountry.phoneCode);
          setSelectedCountry(manualCountry);
        }
      }
      // oxlint-disable-next-line exhaustive-deps
    }, [defaultCountry.mode, defaultCountry.countryCode]);

    useEffect(() => {
      if (defaultCountry.mode === 'auto') {
        // In auto mode, use navigator.language (user's locale) instead of widgetLanguage
        const languageToUse = navigator.language || widgetLanguage || 'en-US';
        const newCountry = getDefaultCountry(languageToUse, countries);
        if (newCountry.countryCode !== selectedCountry.countryCode) {
          countryChangeMetaRef.current.changedByConfigurator = true;
          detectorRef.current.input(newCountry.phoneCode);
          setSelectedCountry(newCountry);
        }
      }
      // oxlint-disable-next-line exhaustive-deps
    }, [widgetLanguage, defaultCountry.mode]);

    const toggleDropdown = useCallback(() => {
      setOpen((prev) => !prev);
    }, []);

    const closeDropdown = useCallback((event?: PointerEvent) => {
      if (
        event &&
        event.target instanceof Node &&
        flagContainerRef.current &&
        flagContainerRef.current.contains(event.target)
      ) {
        return;
      }

      setOpen(false);
    }, []);

    const handleDetectedCountryChange = (countryCode: CountryCode) => {
      const newCountry = countries.find(
        (country) => country.countryCode === countryCode
      ) ?? {
        countryCode,
        name: countryCode,
        phoneCode: `+${getCountryCallingCode(countryCode)}`,
        iconSrc: phoneNumberFlagUrl
      };

      setSelectedCountry(newCountry);
    };

    const handleCountrySelect = (country: Country) => {
      countryChangeMetaRef.current.changedByCountryList = true;
      setSelectedCountry(country);
    };

    return (
      <Container
        ref={mergedRef}
        className={createReadableClass({
          component: 'fields',
          element: 'phone'
        })}
      >
        <PhoneInput
          {...forwardingProps}
          ariaLabelCountryDropdownTrigger={ariaLabelCountryDropdownTrigger}
          fieldColor={fieldColor}
          fieldFillColor={fieldFillColor}
          fieldBorderType={fieldBorderType}
          value={value}
          countryChangeMetaRef={countryChangeMetaRef}
          selectedCountry={selectedCountry}
          fieldBorderRadius={fieldBorderRadius}
          toggleDropdown={toggleDropdown}
          flagContainerRef={flagContainerRef}
          isOpen={open}
          showPlaceholder={showPlaceholder}
          onDetectedCountryChange={handleDetectedCountryChange}
          countryDetector={detectorRef.current}
          ref={forwardingRef}
        />
        <CountryCodeDropdown
          ariaLabel={ariaLabelCountryDropdown}
          fieldColor={fieldColor}
          fieldFillColor={fieldFillColor}
          fieldActiveColor={fieldActiveColor}
          fieldBorderRadius={fieldBorderRadius}
          selectedCountryCode={selectedCountry.countryCode}
          emptyCountryListMessage={emptyCountryListMessage}
          searchPlaceholderText={searchPlaceholderText}
          width={width}
          open={open}
          countries={countries}
          anchorElement={anchorElement}
          onCloseRequest={closeDropdown}
          onSelect={handleCountrySelect}
          portalContainerElement={portalContainerElement}
        />
      </Container>
    );
  }
);

const Container = styled.div``;
